import {
  type BaseFlowDecoration,
  type FlowDecorationData,
  FlowDecorationType,
  type FlowDefinitionState,
  type FlowInputSchema,
  type FlowPricingMode,
  type FlowState,
  type FlowWithOptionalRunStats,
} from 'flow-schema';
import { omit } from 'lodash';
import * as uuid from 'uuid';

import { api, isDefined } from 'common';
import { LLM_MODELS } from 'llm-model-registry';

import {
  convertInputSchemaToParametersConfig,
  convertParametersConfigToInputSchema,
} from './ReactFlow/Nodes/Configs/Start/parameterConfig';
import {
  DEFAULT_START_NODE_POSITION,
  createStartNode,
} from './ReactFlow/Nodes/Configs/Start/utils';
import { compareReactFlowNodes } from './ReactFlow/Nodes/utils';
import {
  AllUiBlockTypes,
  BlockType,
  FlowNodeType,
  type UiPlaceholderBlockType,
  UnknownBlockType,
  getFlowDecorationTypeForFlowNodeType,
  getFlowNodeTypeForFlowDecorationType,
  isBlockType,
  isObsoleteBlockType,
} from './ReactFlow/types';
import {
  type BaseReactFlowNode,
  EdgeHandleType,
  type ReactFlowBlockNode,
  ReactFlowErrorEdgeHandleName,
  type ReactFlowGroupNode,
  type ReactFlowNode,
  type ReactFlowPlaceholderNode,
  type ReactFlowProps,
  createReactFlowEdge,
  isBlockNode,
} from './types';

import AnalyticalFlowMetrics = api.Metric.AnalyticalFlowMetrics;
import RealtimeAgentMetrics = api.Metric.RealtimeAgentMetrics;

export enum FlowTab {
  All = 'All',
  Published = 'Published',
  Review = 'Review',
  Draft = 'Draft',
}

export enum FlowGridRowType {
  Agent = 'Agent',
  Flow = 'Flow',
  Spacer = 'Spacer',
}

export enum ExpansionMode {
  AllExpanded = 'AllExpanded',
  AllCollapsed = 'AllCollapsed',
}

export const ExpansionModeLabels: Record<ExpansionMode, string> = {
  [ExpansionMode.AllExpanded]: 'Collapse All',
  [ExpansionMode.AllCollapsed]: 'Expand All',
} as const;

function isKnownLlmModel(modelKey: string): modelKey is keyof typeof LLM_MODELS {
  return Object.hasOwn(LLM_MODELS, modelKey);
}

export function getLlmModelDisplayName(llmModel: string | null | undefined): string | undefined {
  if (!isDefined(llmModel)) {
    return undefined;
  }
  return isKnownLlmModel(llmModel) ? LLM_MODELS[llmModel].name : llmModel;
}

export function createPlaceholderNode(
  props: Pick<ReactFlowNode, 'position' | 'parentId'> & { uiBlockType: UiPlaceholderBlockType }
): ReactFlowPlaceholderNode {
  const { uiBlockType, parentId, ...nodeProps } = props;
  return {
    id: uuid.v4(),
    data: { uiBlockType },
    type: FlowNodeType.Placeholder,
    parentId,
    extent: isDefined(parentId) ? 'parent' : undefined,
    expandParent: isDefined(parentId) ? true : undefined,
    ...nodeProps,
  };
}

export function convertFlowToReactFlow(
  flowDefinitionData: api.FlowDefinition.FlowData
): ReactFlowProps {
  const { blocks, decorations, edges: flowDefinitionEdges, inputSchema } = flowDefinitionData;
  const parameters = convertInputSchemaToParametersConfig(inputSchema);
  const nodes = blocks.map<ReactFlowNode>((block) => {
    const { uiData } = block;
    return {
      id: block.id,
      position: block.uiData.position,
      sourcePosition: 'right',
      targetPosition: 'left',
      type: FlowNodeType.Block,
      parentId: uiData.groupNodeId,
      extent: isDefined(uiData.groupNodeId) ? 'parent' : undefined,
      expandParent: isDefined(uiData.groupNodeId) ? true : undefined,
      data: {
        uiBlockType: AllUiBlockTypes.has(block.type) ? block.type : UnknownBlockType.Unknown,
        label: block.name,
        description: block.description,
        areExtraTargetHandlesVisible: uiData.areExtraTargetHandlesVisible,
        config: block.type === BlockType.Start ? { ...block.config, parameters } : block.config,
        milestoneName: block.milestoneName,
        events: block.events,
        mockDataEnabled: block.mockDataEnabled,
        mockData: JSON.stringify(block.mockData, null, 2) ?? '',
        stats: block.stats,
        returnType: block.returnType,
      },
    };
  });
  const groupIds = new Set<string>();
  decorations?.nodes.forEach((decorationNode) => {
    const { id, decorationType, position, groupNodeId, dimensions } = decorationNode;
    const baseProps: BaseReactFlowNode = {
      id,
      position,
      type: getFlowNodeTypeForFlowDecorationType(decorationType),
      parentId: groupNodeId,
      extent: isDefined(groupNodeId) ? 'parent' : undefined,
      expandParent: isDefined(groupNodeId) ? true : undefined,
      width: dimensions?.width,
      height: dimensions?.height,
    };
    if (decorationNode.decorationType === FlowDecorationType.StickyNote) {
      nodes.push({
        ...baseProps,
        type: FlowNodeType.StickyNote,
        data: decorationNode.data,
      });
    }
    if (decorationNode.decorationType === FlowDecorationType.GroupNode) {
      nodes.push({
        ...baseProps,
        type: FlowNodeType.GroupNode,
        data: decorationNode.data,
      });
      groupIds.add(decorationNode.id);
    }
  });
  nodes.forEach((node) => {
    if (isDefined(node.parentId) && !groupIds.has(node.parentId)) {
      console.error(`Node ${node.id} has invalid parentId ${node.parentId}, removing parentId`);
      // Remove invalid parentId to avoid issues in React Flow
      node.parentId = undefined;
      node.extent = undefined;
      node.expandParent = undefined;
    }
  });
  const edges: ReactFlowProps['edges'] = flowDefinitionEdges.map((edge) => {
    const { isError } = edge;
    const edgeHandleType = isError ? EdgeHandleType.Error : EdgeHandleType.Default;
    return createReactFlowEdge({
      source: edge.source,
      sourceHandle: isError ? ReactFlowErrorEdgeHandleName : edge.condition,
      target: edge.target,
      edgeHandleType,
    });
  });
  nodes.sort(compareReactFlowNodes);
  return {
    nodes,
    edges,
  };
}

export function convertReactFlowBlockToFlowBlock(
  node: ReactFlowBlockNode
): api.FlowDefinition.FlowBlock | undefined {
  if (!isBlockType(node.data.uiBlockType)) {
    return;
  }
  if (isObsoleteBlockType(node.data.uiBlockType)) {
    return;
  }

  let mockData: unknown;
  if (node.data.mockData) {
    try {
      mockData = JSON.parse(node.data.mockData);
    } catch {
      // Our frontend validation will show this as invalid, but does not
      // prevent saving invalid states. This prevents the bad data from being
      // sent as we can't even construct an invalid representation to send.
      // We can't simply crash because this function is used for dirty checking
      mockData = {
        toJSON: () => {
          throw new Error('Invalid block JSON');
        },
      };
    }
  }

  // Flow data schema is mapped to "parameters" in the Start block config,
  // which is not actually part of the block's config on the back end.
  const config =
    node.data.uiBlockType === BlockType.Start
      ? omit(node.data.config, 'parameters')
      : node.data.config;

  // @ts-expect-error when we exceed 25 blocks, this return fails to recognize the full discriminated union
  return {
    id: node.id,
    uiData: {
      position: node.position,
      groupNodeId: node.parentId,
      areExtraTargetHandlesVisible: node.data.areExtraTargetHandlesVisible,
    },
    type: node.data.uiBlockType,
    name: node.data.label,
    description: node.data.description ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    config: config as any,
    milestoneName: node.data.milestoneName,
    events: node.data.events,
    mockDataEnabled: node.data.mockDataEnabled,
    mockData,
    stats: sanitizeStats(node.data.stats),
  };
}

function sanitizeStats(input: Record<string, string | number> | undefined): Record<string, number> {
  if (!input) {
    return {};
  }
  const output: Record<string, number> = {};
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === 'number') {
      output[key] = value;
    } else if (value !== '') {
      const numberValue = Number(value);
      if (!isNaN(numberValue)) {
        output[key] = numberValue;
      }
    }
  }
  return output;
}

export function convertReactFlowToFlowData(
  reactFlowProps: ReactFlowProps
): api.FlowDefinition.FlowData {
  const blocks: api.FlowDefinition.FlowBlock[] = [];
  const decorations: FlowDecorationData = {
    nodes: [],
  };
  let inputSchema: FlowInputSchema | undefined;
  const blockIds = new Set<string>();
  for (const node of reactFlowProps.nodes) {
    if (isBlockNode(node)) {
      if (node.data.uiBlockType === BlockType.Start) {
        inputSchema = convertParametersConfigToInputSchema(node.data.config?.parameters);
      }
      const block = convertReactFlowBlockToFlowBlock(node);
      if (block) {
        blocks.push(block);
        blockIds.add(node.id);
      }
    } else if (node.type === FlowNodeType.StickyNote || node.type === FlowNodeType.GroupNode) {
      const { id, position, width, height, parentId } = node;
      const baseDecoration: BaseFlowDecoration = {
        id,
        position,
        groupNodeId: parentId,
        decorationType: getFlowDecorationTypeForFlowNodeType(node.type),
      };
      const dimensions = { width, height };
      if (node.type === FlowNodeType.StickyNote) {
        const { data } = node;
        decorations.nodes.push({
          ...baseDecoration,
          decorationType: FlowDecorationType.StickyNote,
          dimensions,
          data,
        });
      } else if (node.type === FlowNodeType.GroupNode) {
        decorations.nodes.push({
          ...baseDecoration,
          decorationType: FlowDecorationType.GroupNode,
          dimensions,
          data: node.data,
        });
      }
    }
  }
  const edges = reactFlowProps.edges
    .map((edge): api.FlowDefinition.FlowEdge => {
      const { edgeHandleType } = edge;
      return {
        source: edge.source,
        target: edge.target,
        condition: edgeHandleType === EdgeHandleType.Default ? edge.sourceHandle : undefined,
        isError: edgeHandleType === EdgeHandleType.Error,
      };
    })
    .filter((edge) => blockIds.has(edge.source) && blockIds.has(edge.target));
  return {
    blocks,
    edges,
    decorations,
    inputSchema,
  };
}

export function createBasicFlowDefinition(): { name: string; data: api.FlowDefinition.FlowData } {
  return {
    name: '',
    data: convertReactFlowToFlowData({
      nodes: [createStartNode({ position: DEFAULT_START_NODE_POSITION })],
      edges: [],
    }),
  };
}

export function getUniqueBlockNameForPrefix(
  prefix: string,
  nodes: (ReactFlowBlockNode | ReactFlowGroupNode)[]
): string {
  const existingBlockNames = nodes.map((node) => node.data.label);
  let name = prefix;
  let i = 1;
  while (existingBlockNames.includes(name)) {
    name = `${prefix}${i}`;
    i++;
  }
  return name;
}

export function getFriendlyEntityType(entityType: string): string {
  switch (entityType) {
    case 'ehrInformation':
      return 'patients';
    case 'encounter':
      return 'encounters';
    case 'patientOrder':
      return 'orders';
    case 'externalDocumentPacket':
      return 'documentPacket';
    default:
      return entityType;
  }
}

export interface FlowGridRow extends Partial<AnalyticalFlowMetrics> {
  id: string;
  path: string[];
  name: string;
  definitionStates: FlowDefinitionState[];
  flowState?: FlowState;
  createdByEmail?: string | null;
  lastRun?: string;
  flowRuns?: number | null;
  agentCategory: string;
  organizationId: string;
  agentId: string;
  rowType: FlowGridRowType;
  tags?: api.FlowDefinition.FlowTag[] | null;
  pricingMode?: FlowPricingMode | null;
}

// Transform flows data into hierarchical tree structure for display
export function mapApiDataToTreeData(args: {
  flows: (FlowWithOptionalRunStats | api.Flow.FlowWithRunCount)[];
  analyticalFlowMetricsByFlowId?: Record<string, AnalyticalFlowMetrics>;
  analyticalAgentMetricsByAgentId?: Record<string, AnalyticalFlowMetrics>;
  realtimeAgentMetricsByAgentId?: Record<string, RealtimeAgentMetrics>;
}): FlowGridRow[] {
  const {
    flows,
    analyticalFlowMetricsByFlowId,
    analyticalAgentMetricsByAgentId,
    realtimeAgentMetricsByAgentId,
  } = args;

  const treeData: FlowGridRow[] = [];
  const processedAgentIds = new Set<string>();

  flows.forEach((flow) => {
    const agentId = flow.agentId ?? 'noAssociatedAgent'; // Some legacy flows might not have an agent.
    const agentCategory = flow.agentCategory ?? 'Placeholder';

    if (!processedAgentIds.has(agentId)) {
      const analyticalAgentMetrics = analyticalAgentMetricsByAgentId?.[agentId];
      const agentRealtimeMetrics = realtimeAgentMetricsByAgentId?.[agentId];

      treeData.push({
        id: agentId,
        path: [agentId],
        name: flow.agentName || 'No Associated Agent',
        definitionStates: [],
        agentCategory,
        organizationId: flow.organizationId,
        agentId,
        rowType: FlowGridRowType.Agent,
        dollarValueGenerated: analyticalAgentMetrics?.dollarValueGenerated,
        utilizationDollars: analyticalAgentMetrics?.utilizationDollars,
        // Map realtime metrics to existing fields for display
        lastRun: agentRealtimeMetrics?.mostRecentFlowRunAt,
        flowRuns: agentRealtimeMetrics?.flowRunCount,
      });

      processedAgentIds.add(agentId);
    }

    const analyticalFlowMetrics = analyticalFlowMetricsByFlowId?.[flow.id];
    treeData.push({
      id: flow.id,
      path: [agentId, flow.id],
      name: flow.name,
      definitionStates: flow.flowDefinitionStates || [],
      flowState: flow.state,
      createdByEmail: flow.createdByEmail,
      lastRun: flow.mostRecentRunAt,
      flowRuns: flow.runCount ?? 0,
      agentCategory,
      organizationId: flow.organizationId,
      agentId,
      rowType: FlowGridRowType.Flow,
      dollarValueGenerated: analyticalFlowMetrics?.dollarValueGenerated,
      utilizationDollars: analyticalFlowMetrics?.utilizationDollars,
      tags: flow.tags,
      pricingMode: flow.pricingMode,
    });
  });

  // Adding spacer rows after each agent group except the last one
  const agentIds = Array.from(processedAgentIds);
  for (let i = 0; i < agentIds.length - 1; i++) {
    treeData.push({
      id: `spacer-after-${agentIds[i]}`,
      path: [agentIds[i], 'spacer'],
      name: '',
      definitionStates: [],
      agentCategory: '',
      organizationId: '',
      agentId: agentIds[i],
      rowType: FlowGridRowType.Spacer,
    });
  }

  return treeData;
}

/**
 * Extract PHI free data to log as an event
 * @param flowDefinition - The flow definition to extract data from.
 * @returns The properties for the flow definition event.
 * This function never returns data with PHI or PII content.
 */
export function getFlowDefinitionEventProperties(
  flowDefinition: Pick<api.FlowDefinition.FlowDefinitionWithFlow, 'flowId' | 'name' | 'version'>
) {
  return {
    flowId: flowDefinition.flowId,
    flowName: flowDefinition.name,
    flowDefinitionVersion: flowDefinition.version,
  };
}
